import {FieldArray, Form, FormikProvider, useFormik} from "formik"
import * as Yup from "yup"
import Divider from "@mui/material/Divider"
import React from "react"
import Grid from "@mui/material/Grid"
import Stack from "@mui/material/Stack"
import InputLabel from "@mui/material/InputLabel"
import TextField from "@mui/material/TextField"
import Button from "@mui/material/Button"
import DialogTitle from "@mui/material/DialogTitle"
import {DialogActions, DialogContent, IconButton} from "@mui/material"
import {useAppDispatch} from "reducers/hooks"
import {boxes, saveElement, ShedElement, updateElement} from "reducers/Shed/ShedReducer"
import {Add, BoxRemove} from "iconsax-react"
import {FormattedMessage, useIntl} from "react-intl"
import {ColumnFiltersState} from "@tanstack/react-table";

interface ShedFormProps {
    closeModal: () => void;
    element: ShedElement | null;
    onSuccess?: () => void;
    pageableData?: {
        page: number;
        rowsPerPage: number;
        filters: ColumnFiltersState | null;
    };
}


export const ShedForm: React.FC<ShedFormProps> = ({
                                                      closeModal,
                                                      element,
                                                      onSuccess,
                                                      pageableData}) => {
    const dispatch = useAppDispatch()
    const [removedBoxes, setRemovedBoxes] = React.useState<boxes[]>([]);
    const intl = useIntl()

    const formik = useFormik({
        initialValues: element ? {
            // Per l'update
            _id: element._id,
            name: element.name,
            boxes: element?.boxes?.map((box) => ({
                _id: box._id || '',
                name: box.name || '',
                maxCapacity: box.maxCapacity || 0
            })) || []
        } : {
            _id: '',
            name: '',
            boxes: []
        },
        validationSchema: Yup.object().shape({
            name: Yup.string()
              .min(1, intl.formatMessage({ id: "Name length not valid" }))
              .required(intl.formatMessage({ id: "Name is required" })),

            boxes: Yup.array().of(
              Yup.object().shape({
                name: Yup.string().required(intl.formatMessage({ id: "Box name is required" })),

                maxCapacity: Yup.number()
                  .min(1, intl.formatMessage({ id: "Max capacity must be at least 1" }))
                  .required(intl.formatMessage({ id: "Max capacity is required" }))
                })
            ).required(),
        }),
        enableReinitialize: true,
        onSubmit: async (values) => {
            try {
                // Prepara i box finali
                const updatedBoxes = values.boxes.map((box) => {
                    if (!box._id) {
                        return { _id: '',name: box.name, maxCapacity: box.maxCapacity }
                    }
                    return box
                })

                const updatePayload: ShedElement = {
                    _id: element?._id || '',
                    name: values.name,
                    boxes: updatedBoxes || [],
                }
                if (element && element._id) {
                    dispatch(updateElement({
                        shed: updatePayload,
                        callback: () => {
                            closeModal();
                            onSuccess?.();
                        }
                    }))
                } else {
                    dispatch(saveElement({
                        shed: updatePayload,
                        callback: () => {
                            closeModal();
                            onSuccess?.();
                        }
                    }))
                }
                closeModal()
            } catch (error) {
                console.error('Error:', error)
            }
        }

    })

    return (
        <>
            <FormikProvider value={formik}>
                <Form autoComplete="off" noValidate onSubmit={formik.handleSubmit}>
                    <DialogTitle>{intl.formatMessage({id: "NewShed"})}</DialogTitle>
                    <Divider />
                    <DialogContent sx={{ p: 2.5 }}>
                        <Grid container spacing={3}>

                            {/* Shed Name */}
                            <Grid item xs={12}>
                                <Stack spacing={1}>
                                    <InputLabel htmlFor="name"><FormattedMessage id="ShedName" defaultMessage="ShedName" /></InputLabel>
                                    <TextField
                                        fullWidth
                                        id="name"
                                        placeholder={intl.formatMessage({id: "ShedName"})}
                                        {...formik.getFieldProps('name')}
                                        error={Boolean(formik.touched.name && formik.errors.name)}
                                        helperText={formik.touched.name && formik.errors.name}
                                    />
                                </Stack>
                            </Grid>

                            {/* Boxes Section */}
                            <Grid item xs={12}>
                                <FieldArray name="boxes">
                                    {({ push, remove }) => (
                                        <Stack spacing={2}>
                                            <InputLabel>Boxes</InputLabel>
                                            {formik.values.boxes.length > 0 &&
                                                formik.values.boxes.map((box, index) => (
                                                    <Stack direction="row" spacing={2} alignItems="center" key={index}>
                                                        <TextField
                                                            fullWidth
                                                            placeholder={intl.formatMessage({id: "Boxname"})}
                                                            {...formik.getFieldProps(`boxes[${index}].name`)}
                                                            error={Boolean(formik.touched.boxes?.[index]?.name)}
                                                            helperText={formik.touched.boxes?.[index]?.name}
                                                            disabled={Boolean(element && box._id)}
                                                        />
                                                        <TextField
                                                            fullWidth
                                                            placeholder="Max Capacity"
                                                            type="number"
                                                            {...formik.getFieldProps(`boxes[${index}].maxCapacity`)}
                                                            error={Boolean(formik.touched.boxes?.[index]?.maxCapacity)}
                                                            helperText={formik.touched.boxes?.[index]?.maxCapacity}
                                                            disabled={Boolean(element && box._id)}
                                                        />
                                                        <IconButton
                                                            color="error"
                                                            onClick={() => {
                                                                const boxToRemove = formik.values.boxes[index];
                                                                if (boxToRemove._id) {
                                                                    setRemovedBoxes((prev) => [...prev, boxToRemove]);
                                                                }
                                                                remove(index); 
                                                            }}
                                                            disabled={formik.values.boxes.length === 0}
                                                        >
                                                            <BoxRemove />
                                                        </IconButton>
                                                    </Stack>
                                                ))}

                                            <Button
                                                variant="outlined"
                                                startIcon={<Add />}
                                                onClick={() => push({ name: '', maxCapacity: 0 })}
                                            >
                                               <FormattedMessage id="Addbox" defaultMessage="Addbox" />
                                            </Button>
                                        </Stack>
                                    )}
                                </FieldArray>
                            </Grid>
                        </Grid>
                    </DialogContent>
                    <Divider />
                    <DialogActions sx={{ p: 2.5 }}>
                        <Grid container justifyContent="flex-end" alignItems="center">
                            <Grid item>
                                <Stack direction="row" spacing={2} alignItems="center">
                                    <Button color="error" onClick={closeModal}>
                                    <FormattedMessage id="Cancel" defaultMessage="Cancel" />
                                    </Button>
                                    <Button type="submit" variant="contained">
                                    <FormattedMessage id="Save" defaultMessage="Save" />
                                    </Button>
                                </Stack>
                            </Grid>
                        </Grid>
                    </DialogActions>
                </Form>
            </FormikProvider>
        </>
    );
}
